import React, { useEffect, useState } from "react";
import { array, bool, func, object, string } from "prop-types";
import { get, last, nth } from "lodash";
import { ResponsiveImage } from "@quintype/components";
import { useSelector } from "react-redux";

import { SvgIconHandler } from "../../atoms";
import SummaryCard from "../../molecules/SummaryCard";
import { useDeviceType } from "../../customHooks/useDeviceType";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { STATIC_KEYWORDS } from "../../../constants";
import { getTemplateName } from "../../../ga-utils";

import "./summary-page.m.css";

const ImageStack = ({ stories }) => {
  return (
    <div styleName="image-stack">
      {stories?.map((story, index) => {
        return (
          <div
            key={index}
            styleName="image-wrapper"
            style={{
              top: `${index * 3}px`,
              left: `${index * 4}px`,
            }}
          >
            <ResponsiveImage
              slug={story.story ? story?.story["hero-image-s3-key"] : story["hero-image-s3-key"]}
              aspectRatio={[16, 9]}
              defaultWidth={62}
              imgParams={{ auto: ["format", "compress"], q: 70 }}
              shouldDecodeImage={true}
            />
          </div>
        );
      })}
    </div>
  );
};

ImageStack.propTypes = {
  stories: array,
};
const SummaryMenu = ({ menuGroup, handleCategoryClick, activeCategory, isDesktop, summarySponsor }) => {
  return (
    <div styleName="summary-menu">
      <div styleName="top-header">
        <button
          styleName="close-btn"
          onClick={() => {
            const directVisit = document.referrer === "" || window.history.length <= 1;

            if (directVisit) {
              window.location.href = "/";
            } else {
              window.history.back();
            }
          }}
        >
          <SvgIconHandler
            type={isDesktop ? "left-arrow" : "close"}
            width={isDesktop ? "16" : "12"}
            height={isDesktop ? "16" : "12"}
          />
        </button>
        <p styleName="welcome">{STATIC_KEYWORDS.WELCOME}</p>
        <h1 styleName="title">{STATIC_KEYWORDS.OPEN_SUMMARIES}</h1>
        <p styleName="subtitle">{STATIC_KEYWORDS.SUMMARY_SUBTITLE}</p>
        <div styleName="summary-icon-wrapper">
          <span styleName="summary-icon">
            <SvgIconHandler type={"summary"} width="18" height="18" />
          </span>
        </div>
        <h2 styleName="categories-text">{STATIC_KEYWORDS.SELECT_CATEGORY}</h2>
      </div>
      <div styleName="categories">
        {menuGroup?.map((menu, index) => {
          return menu?.items?.length ? (
            <div key={index} styleName="category-card" onClick={() => handleCategoryClick(menu?.slug)}>
              <div styleName="left-content">
                <p styleName={`name ${activeCategory === menu.slug ? "active" : ""}`}>{menu?.name}</p>
                <p styleName="number">{menu?.items?.length} Stories</p>
              </div>
              {!isDesktop && (
                <div styleName="right-content">
                  <ImageStack stories={menu?.items?.slice(0, 6)} />
                </div>
              )}
            </div>
          ) : null;
        })}
      </div>
      {summarySponsor ? (
        <div styleName="bottom-sponsor-text">
          <span>{STATIC_KEYWORDS.POWERED_BY}</span>
          {" X"}
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

SummaryMenu.propTypes = {
  menuGroup: array,
  handleCategoryClick: func,
  activeCategory: string,
  isDesktop: bool,
  summarySponsor: bool,
};

const SummaryPage = (props) => {
  const checkSummary = (cards) => {
    let summaryPresent = false;
    if (cards) {
      if (last(last(cards)["story-elements"])?.subtype === "summary") {
        summaryPresent = true;
      } else if (last(last(cards)["story-elements"])?.metadata?.["promotional-message"] === true) {
        if (nth(last(cards)["story-elements"], -2)?.subtype === "summary") {
          summaryPresent = true;
        }
      }
    }
    return summaryPresent;
  };

  const filterSummaryStories = (item) => {
    const updated = item?.items?.filter((it, ind) => checkSummary(it?.story?.cards));
    return { ...item, items: updated };
  };

  const allStories = props?.data?.collection?.items
    .filter((item, index) => item?.type === "story" && checkSummary(item?.story?.cards))
    .map((item) => item.story);
  const menuGroup = props?.data?.collection?.items
    .filter((item, index) => item?.type === "collection")
    .map((item, ind) => filterSummaryStories(item));
  const updatedGroup = [{ items: allStories, slug: "all-stories", name: "All Stories" }, ...menuGroup];

  const { summary_sponsor: summarySponsor } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes"], false)
  );

  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));
  const [showSummary, setShowSummary] = useState(false);
  const [collectionStories, setCollectionStories] = useState(allStories);
  const [activeCategory, setActiveCategory] = useState("all-stories");
  const isDesktop = useDeviceType() === "desktop";

  // Track category clicks for the SummaryMenu component
  const trackCategoryClick = (slug) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "short_news_category_click",
        link_text: slug,
        category: "short-news",
        sub_category: "NA",
        content_id: "NA",
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleCategoryClick = (slug) => {
    // Track the category click event
    trackCategoryClick(slug);

    categoryLandingDL(slug);
    if (slug === "all-stories") {
      setCollectionStories(allStories);
    } else {
      const collectionGroup = updatedGroup.find((item, index) => item.slug === slug);
      const collectionStories = collectionGroup?.items
        ?.filter((item, ind) => item.type === "story" && checkSummary(item?.story?.cards))
        ?.map((item, ind) => item?.story);
      setCollectionStories(collectionStories);
    }
    setShowSummary(true);
    setActiveCategory(slug);
  };

  const categoryLandingDL = (categoryName) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "short-news",
      sub_category: categoryName,
      content_id: "NA",
      template_name: "listing",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in",
      subscription_status: "not-subscribed",
      ad_free: false,
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  };

  useEffect(() => {
    categoryLandingDL("NA");
    isDesktop && categoryLandingDL("all-stories");
  }, []);
  const isAdfree = props?.config?.subscriber === "true";
  const summaryWrapperStyle = isAdfree ? "summary-wrapper-no-ad" : "ad-comp";

  // Auto-scroll to summary story via slug
  useEffect(() => {
    if (collectionStories.length === 0) return;
    const params = window && new URLSearchParams(window.location.search);
    const slugFromUrl = params?.get("slug");
    if (slugFromUrl) {
      const pathParts = slugFromUrl.split("/");
      const publicIdFromUrl = pathParts[pathParts.length - 1];
      const slugUrl = pathParts[0];
      if (publicIdFromUrl) {
        const index = collectionStories.findIndex((s) => s?.["public-identifier"] === publicIdFromUrl);
        if (index < 0) {
          const index = menuGroup.findIndex((s) => s?.slug === slugUrl);
          if (index >= 0) {
            handleCategoryClick(slugUrl);
          } else {
            handleCategoryClick("all-stories");
          }
        } else {
          handleCategoryClick("all-stories");
        }
      }
    }
  }, []);

  // Auto-scroll to summary story via slug
  useEffect(() => {
    if (collectionStories.length === 0) return;
    const params = window && new URLSearchParams(window.location.search);
    const slugFromUrl = params?.get("slug");
    if (slugFromUrl) {
      const pathParts = slugFromUrl.split("/");
      const publicIdFromUrl = pathParts[pathParts.length - 1];
      const slugUrl = pathParts[0];
      if (publicIdFromUrl) {
        const index = collectionStories.findIndex((s) => s?.slug === slugFromUrl);
        if (index < 0) {
          const index = menuGroup.findIndex((s) => s?.slug === slugUrl);
          if (index >= 0) {
            handleCategoryClick(slugUrl);
          } else {
            handleCategoryClick("all-stories");
          }
        } else {
          handleCategoryClick("all-stories");
        }
      }
    }
  }, []);

  return (
    <div styleName="summary-page-container">
      {(!showSummary || isDesktop) && (
        <SummaryMenu
          menuGroup={updatedGroup}
          handleCategoryClick={handleCategoryClick}
          activeCategory={activeCategory}
          isDesktop={isDesktop}
          summarySponsor={summarySponsor}
        />
      )}
      {(showSummary || isDesktop) && collectionStories && (
        <SummaryCard
          setShowSummary={setShowSummary}
          menuGroup={menuGroup}
          story={collectionStories[0]}
          collectionStories={collectionStories.slice(1, collectionStories.length)}
          pageType="summary-page"
        />
      )}
      <div styleName={summaryWrapperStyle}>
        {!isAdfree && (
          <ListingAdSlot
            adStyleName="ad-slot-size-300x250"
            adType="right_side"
            pageType="summary-page"
            adSlotConfigName="mRec_ad"
          />
        )}
      </div>
    </div>
  );
};

SummaryPage.propTypes = {
  props: object,
};

export { SummaryPage };
